import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  Lock, Users, Trophy, BarChart3, Trash2, DollarSign, Activity, 
  Download, Search, Tag, Plus, X, Calendar, Percent, Gift,
  TrendingUp, ChevronDown, ChevronUp, Edit, Ban, Clock
} from 'lucide-react';
import axios from 'axios';
import { format } from 'date-fns';
import { SPORTS_CONFIG, ENABLED_SPORTS } from '../config/sports';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Sports limits per plan
const PLAN_SPORTS_LIMITS = {
  '2_sports_monthly': 2, '2_sports_yearly': 2,
  '4_sports_monthly': 4, '4_sports_yearly': 4,
  'all_sports_monthly': 6, 'all_sports_yearly': 6,
};

export default function Admin() {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [token, setToken] = useState(localStorage.getItem('admin_token'));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [revenue, setRevenue] = useState(null);
  const [activity, setActivity] = useState(null);
  const [discountCodes, setDiscountCodes] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editingSports, setEditingSports] = useState([]);

  // New discount code form
  const [newCode, setNewCode] = useState({
    code: '',
    discount_percent: 10,
    max_uses: '',
    expires_at: '',
    free_trial_games: '',
    is_free_account: false,
    free_account_days: 365
  });

  useEffect(() => {
    if (token) {
      setIsLoggedIn(true);
      fetchAllData();
    }
  }, [token]);

  const api = axios.create({
    baseURL: `${BACKEND_URL}/api`,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });

  const fetchAllData = async () => {
    await Promise.all([
      fetchStats(),
      fetchUsers(),
      fetchRevenue(),
      fetchActivity(),
      fetchDiscountCodes()
    ]);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/login`, { email, password });
      const { access_token } = response.data;
      localStorage.setItem('admin_token', access_token);
      setToken(access_token);
      setIsLoggedIn(true);
      toast.success('Admin login successful');
    } catch (error) {
      toast.error('Invalid admin credentials');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setToken(null);
    setIsLoggedIn(false);
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/admin/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/admin/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchRevenue = async () => {
    try {
      const response = await api.get('/admin/revenue');
      setRevenue(response.data);
    } catch (error) {
      console.error('Failed to fetch revenue:', error);
    }
  };

  const fetchActivity = async () => {
    try {
      const response = await api.get('/admin/activity');
      setActivity(response.data);
    } catch (error) {
      console.error('Failed to fetch activity:', error);
    }
  };

  const fetchDiscountCodes = async () => {
    try {
      const response = await api.get('/admin/discount-codes');
      setDiscountCodes(response.data);
    } catch (error) {
      console.error('Failed to fetch discount codes:', error);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchResults(null);
      return;
    }
    try {
      const response = await api.get(`/admin/users/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchResults(response.data);
    } catch (error) {
      toast.error('Search failed');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user and all their data?')) return;
    
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success('User deleted');
      fetchUsers();
      fetchStats();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const handleCleanupTestUsers = async () => {
    if (!window.confirm('This will permanently delete ALL test users (@test.com, loadtest_, test_, etc.) and their data. Are you sure?')) return;
    
    try {
      const response = await api.delete('/admin/cleanup-test-users');
      toast.success(`Cleaned up ${response.data.deleted_users} test users, ${response.data.deleted_athletes} athletes, ${response.data.deleted_games} games`);
      fetchUsers();
      fetchStats();
    } catch (error) {
      toast.error('Failed to clean up test users');
    }
  };

  const handleExport = async (type) => {
    try {
      const response = await api.get(`/admin/export/${type}`);
      const blob = new Blob([response.data.csv_data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type}_export_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      toast.success(`Exported ${response.data.count} ${type}`);
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const handleCreateDiscountCode = async () => {
    if (!newCode.code.trim()) {
      toast.error('Please enter a code');
      return;
    }
    try {
      await api.post('/admin/discount-codes', {
        code: newCode.code,
        discount_percent: newCode.is_free_account ? 100 : parseInt(newCode.discount_percent),
        max_uses: newCode.max_uses ? parseInt(newCode.max_uses) : null,
        expires_at: newCode.expires_at || null,
        free_trial_games: newCode.free_trial_games ? parseInt(newCode.free_trial_games) : null,
        is_free_account: newCode.is_free_account,
        free_account_days: newCode.is_free_account ? parseInt(newCode.free_account_days) : null
      });
      toast.success(newCode.is_free_account ? 'Free account code created!' : 'Discount code created');
      setShowDiscountModal(false);
      setNewCode({ code: '', discount_percent: 10, max_uses: '', expires_at: '', free_trial_games: '', is_free_account: false, free_account_days: 365 });
      fetchDiscountCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create code');
    }
  };

  const handleToggleDiscountCode = async (codeId, currentActive) => {
    try {
      await api.put(`/admin/discount-codes/${codeId}`, { is_active: !currentActive });
      toast.success(currentActive ? 'Code deactivated' : 'Code activated');
      fetchDiscountCodes();
    } catch (error) {
      toast.error('Failed to update code');
    }
  };

  const handleDeleteDiscountCode = async (codeId) => {
    if (!window.confirm('Delete this discount code?')) return;
    try {
      await api.delete(`/admin/discount-codes/${codeId}`);
      toast.success('Code deleted');
      fetchDiscountCodes();
    } catch (error) {
      toast.error('Failed to delete code');
    }
  };

  const handleManageSubscription = async (action, days = null, newPlan = null) => {
    if (!selectedUser) return;
    try {
      await api.put(`/admin/users/${selectedUser.id}/subscription`, {
        action,
        days: days ? parseInt(days) : null,
        new_plan: newPlan
      });
      toast.success(`Subscription ${action} successful`);
      setShowUserModal(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update subscription');
    }
  };

  const handleUpdateUserSports = async () => {
    if (!selectedUser) return;
    try {
      await api.put(`/admin/users/${selectedUser.id}/sports`, {
        selected_sports: editingSports
      });
      toast.success('Sports updated successfully');
      setShowUserModal(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update sports');
    }
  };

  const handleToggleUserSport = (sport) => {
    const limit = PLAN_SPORTS_LIMITS[selectedUser?.subscription_type] || 6;
    setEditingSports(prev => {
      if (prev.includes(sport)) {
        return prev.filter(s => s !== sport);
      }
      if (prev.length >= limit) {
        toast.error(`This plan allows only ${limit} sports`);
        return prev;
      }
      return [...prev, sport];
    });
  };

  const openUserModal = (user) => {
    setSelectedUser(user);
    setEditingSports(user.selected_sports || []);
    setShowUserModal(true);
  };

  const handleArchiveUser = async () => {
    if (!selectedUser) return;
    try {
      await api.put(`/admin/users/${selectedUser.id}/archive`);
      toast.success('Account archived');
      setShowUserModal(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to archive account');
    }
  };

  // Login Screen
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-[#000] flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Lock className="w-12 h-12 text-[#00E5E5] mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white">Admin Portal</h1>
            <p className="text-gray-400">In The Stands Management</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <input
              type="email"
              placeholder="Admin Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-[#1A1A1A] text-white rounded-xl py-4 px-4 focus:outline-none focus:ring-2 focus:ring-[#00E5E5]"
            />
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-[#1A1A1A] text-white rounded-xl py-4 px-4 focus:outline-none focus:ring-2 focus:ring-[#00E5E5]"
            />
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-[#00E5E5] text-black font-bold py-4 rounded-xl disabled:opacity-50"
            >
              {isLoading ? 'Logging in...' : 'Login'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-[#000] pb-8">
      {/* Header */}
      <div className="p-4 flex justify-between items-center border-b border-[#333]">
        <h1 className="text-xl font-bold text-white">Admin Dashboard</h1>
        <button onClick={handleLogout} className="text-gray-400 hover:text-white">
          Logout
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-[#333] overflow-x-auto">
        {['overview', 'users', 'revenue', 'activity', 'discounts'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 min-w-[70px] py-3 px-2 font-semibold capitalize whitespace-nowrap text-sm ${
              activeTab === tab ? 'text-[#00E5E5] border-b-2 border-[#00E5E5]' : 'text-gray-400'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      <div className="p-4">
        {/* Overview Tab */}
        {activeTab === 'overview' && stats && (
          <div className="space-y-4">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-[#1A1A1A] rounded-2xl p-4 text-center">
                <Users className="w-8 h-8 text-[#00E5E5] mx-auto mb-2" />
                <p className="text-3xl font-bold text-white">{stats.total_users}</p>
                <p className="text-sm text-gray-400">Total Users</p>
              </div>
              <div className="bg-[#1A1A1A] rounded-2xl p-4 text-center">
                <Trophy className="w-8 h-8 text-[#FF9500] mx-auto mb-2" />
                <p className="text-3xl font-bold text-white">{stats.total_athletes}</p>
                <p className="text-sm text-gray-400">Athletes</p>
              </div>
              <div className="bg-[#1A1A1A] rounded-2xl p-4 text-center">
                <BarChart3 className="w-8 h-8 text-[#4ADE80] mx-auto mb-2" />
                <p className="text-3xl font-bold text-white">{stats.total_games}</p>
                <p className="text-sm text-gray-400">Games</p>
              </div>
              <div className="bg-[#1A1A1A] rounded-2xl p-4 text-center">
                <DollarSign className="w-8 h-8 text-[#BB86FC] mx-auto mb-2" />
                <p className="text-3xl font-bold text-white">{stats.subscribed_users}</p>
                <p className="text-sm text-gray-400">Subscribed</p>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-[#1A1A1A] rounded-2xl p-4">
              <h2 className="text-white font-semibold mb-3">Quick Actions</h2>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => handleExport('users')}
                  className="flex items-center gap-2 px-4 py-2 bg-[#333] rounded-lg text-white hover:bg-[#444]"
                >
                  <Download className="w-4 h-4" /> Export Users
                </button>
                <button
                  onClick={() => handleExport('games')}
                  className="flex items-center gap-2 px-4 py-2 bg-[#333] rounded-lg text-white hover:bg-[#444]"
                >
                  <Download className="w-4 h-4" /> Export Games
                </button>
                <button
                  onClick={() => handleExport('marketing-list')}
                  className="flex items-center gap-2 px-4 py-2 bg-[#333] rounded-lg text-white hover:bg-[#444]"
                  data-testid="export-marketing-list-btn"
                >
                  <Download className="w-4 h-4" /> Export Marketing List
                </button>
                <button
                  onClick={() => setActiveTab('discounts')}
                  className="flex items-center gap-2 px-4 py-2 bg-[#00E5E5] rounded-lg text-black font-semibold"
                >
                  <Tag className="w-4 h-4" /> Manage Discounts
                </button>
              </div>
            </div>

            {/* Games by Sport */}
            <div className="bg-[#1A1A1A] rounded-2xl p-4">
              <h2 className="text-white font-semibold mb-3">Games by Sport</h2>
              <div className="space-y-2">
                {Object.entries(stats.games_by_sport || {}).map(([sport, count]) => {
                  const config = SPORTS_CONFIG[sport];
                  const percent = stats.total_games > 0 ? (count / stats.total_games) * 100 : 0;
                  return (
                    <div key={sport} className="flex items-center gap-3">
                      <span className="text-xl w-8">{config?.emoji}</span>
                      <span className="text-gray-300 w-24">{config?.name}</span>
                      <div className="flex-1 bg-[#333] rounded-full h-2">
                        <div 
                          className="bg-[#00E5E5] h-2 rounded-full" 
                          style={{ width: `${percent}%` }}
                        />
                      </div>
                      <span className="text-white font-semibold w-12 text-right">{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Recent Games */}
            <div className="bg-[#1A1A1A] rounded-2xl p-4">
              <h2 className="text-white font-semibold mb-3">Recent Games</h2>
              <div className="space-y-2">
                {stats.recent_games?.slice(0, 5).map(game => (
                  <div key={game.id} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <span>{SPORTS_CONFIG[game.sport]?.emoji}</span>
                      <span className="text-gray-300">{game.athlete_name}</span>
                    </div>
                    <span className="text-[#00E5E5]">{game.points} pts</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-4">
            {/* Search and Cleanup Actions */}
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="flex-1 flex gap-2">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search users by email..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    className="w-full bg-[#1A1A1A] text-white rounded-xl py-3 pl-10 pr-4 focus:outline-none focus:ring-2 focus:ring-[#00E5E5]"
                  />
                </div>
                <button
                  onClick={handleSearch}
                  className="px-4 py-3 bg-[#00E5E5] text-black font-semibold rounded-xl whitespace-nowrap"
                >
                  Search
                </button>
              </div>
              <button
                onClick={handleCleanupTestUsers}
                className="px-4 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl whitespace-nowrap"
                data-testid="cleanup-test-users-btn"
              >
                🗑️ Clean Test Users
              </button>
            </div>

            {/* Search Results or All Users */}
            <div className="space-y-2">
              {(searchResults || users).map(user => (
                <div key={user.id} className="bg-[#1A1A1A] rounded-xl p-4">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-semibold text-sm sm:text-base truncate">{user.email}</p>
                      <p className="text-xs text-gray-400">
                        Joined: {format(new Date(user.created_at), 'MMM d, yyyy')}
                      </p>
                      <div className="flex gap-4 mt-2 text-xs">
                        <span className="text-gray-400">{user.athlete_count} athletes</span>
                        <span className="text-gray-400">{user.game_count} games</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {user.is_subscribed && (
                        <span className="bg-[#4ADE80] text-black text-xs font-bold px-2 py-1 rounded">
                          PRO
                        </span>
                      )}
                      <button
                        onClick={() => openUserModal(user)}
                        className="p-2 text-gray-400 hover:text-[#00E5E5]"
                        title="Manage"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteUser(user.id)}
                        className="p-2 text-gray-400 hover:text-red-500"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  {user.is_subscribed && user.subscription_expires && (
                    <p className="text-xs text-gray-500 mt-2">
                      Expires: {format(new Date(user.subscription_expires), 'MMM d, yyyy')}
                    </p>
                  )}
                </div>
              ))}
            </div>

            {searchResults && (
              <button
                onClick={() => { setSearchResults(null); setSearchQuery(''); }}
                className="text-[#00E5E5] text-sm"
              >
                ← Show all users
              </button>
            )}
          </div>
        )}

        {/* Revenue Tab */}
        {activeTab === 'revenue' && revenue && (
          <div className="space-y-4">
            {/* Revenue Stats */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-gradient-to-br from-[#00E5E5]/20 to-[#1A1A1A] rounded-2xl p-4 text-center">
                <DollarSign className="w-8 h-8 text-[#00E5E5] mx-auto mb-2" />
                <p className="text-3xl font-bold text-white">${revenue.total_revenue}</p>
                <p className="text-sm text-gray-400">Total Revenue</p>
              </div>
              <div className="bg-[#1A1A1A] rounded-2xl p-4 text-center">
                <TrendingUp className="w-8 h-8 text-[#4ADE80] mx-auto mb-2" />
                <p className="text-3xl font-bold text-white">{revenue.total_transactions}</p>
                <p className="text-sm text-gray-400">Transactions</p>
              </div>
            </div>

            {/* Revenue by Plan */}
            <div className="bg-[#1A1A1A] rounded-2xl p-4">
              <h2 className="text-white font-semibold mb-3">Revenue by Plan</h2>
              <div className="space-y-3">
                {Object.entries(revenue.revenue_by_plan || {}).map(([plan, data]) => (
                  <div key={plan} className="flex justify-between items-center">
                    <span className="text-gray-300 capitalize">{plan.replace(/_/g, ' ')}</span>
                    <div className="text-right">
                      <p className="text-white font-semibold">${data.revenue.toFixed(2)}</p>
                      <p className="text-xs text-gray-500">{data.count} sales</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Active Subscriptions */}
            <div className="bg-[#1A1A1A] rounded-2xl p-4">
              <h2 className="text-white font-semibold mb-3">Active Subscriptions</h2>
              <div className="space-y-2">
                {Object.entries(revenue.subscription_breakdown || {}).map(([plan, count]) => (
                  <div key={plan} className="flex justify-between items-center">
                    <span className="text-gray-300 capitalize">{plan.replace(/_/g, ' ')}</span>
                    <span className="text-[#00E5E5] font-semibold">{count}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Monthly Revenue Chart (Simple) */}
            {Object.keys(revenue.monthly_revenue || {}).length > 0 && (
              <div className="bg-[#1A1A1A] rounded-2xl p-4">
                <h2 className="text-white font-semibold mb-3">Monthly Revenue</h2>
                <div className="space-y-2">
                  {Object.entries(revenue.monthly_revenue || {})
                    .sort((a, b) => b[0].localeCompare(a[0]))
                    .slice(0, 6)
                    .map(([month, amount]) => (
                      <div key={month} className="flex justify-between items-center">
                        <span className="text-gray-400">{month}</span>
                        <span className="text-white font-semibold">${amount.toFixed(2)}</span>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Activity Tab */}
        {activeTab === 'activity' && activity && (
          <div className="space-y-4">
            {/* Activity Stats */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-[#1A1A1A] rounded-2xl p-4">
                <p className="text-sm text-gray-400 mb-1">New Users (7 days)</p>
                <p className="text-2xl font-bold text-[#00E5E5]">{activity.new_users_week}</p>
              </div>
              <div className="bg-[#1A1A1A] rounded-2xl p-4">
                <p className="text-sm text-gray-400 mb-1">New Users (30 days)</p>
                <p className="text-2xl font-bold text-white">{activity.new_users_month}</p>
              </div>
              <div className="bg-[#1A1A1A] rounded-2xl p-4">
                <p className="text-sm text-gray-400 mb-1">Games (7 days)</p>
                <p className="text-2xl font-bold text-[#4ADE80]">{activity.games_week}</p>
              </div>
              <div className="bg-[#1A1A1A] rounded-2xl p-4">
                <p className="text-sm text-gray-400 mb-1">Games (30 days)</p>
                <p className="text-2xl font-bold text-white">{activity.games_month}</p>
              </div>
            </div>

            {/* Daily Signups Chart */}
            <div className="bg-[#1A1A1A] rounded-2xl p-4">
              <h2 className="text-white font-semibold mb-3">Daily Signups (14 days)</h2>
              <div className="flex items-end gap-1 h-32">
                {Object.entries(activity.daily_signups || {})
                  .sort((a, b) => a[0].localeCompare(b[0]))
                  .map(([date, count]) => {
                    const maxCount = Math.max(...Object.values(activity.daily_signups || {}), 1);
                    const height = (count / maxCount) * 100;
                    return (
                      <div key={date} className="flex-1 flex flex-col items-center gap-1">
                        <div 
                          className="w-full bg-[#00E5E5] rounded-t min-h-[4px]"
                          style={{ height: `${height}%` }}
                          title={`${date}: ${count} signups`}
                        />
                        <span className="text-[8px] text-gray-500 -rotate-45 origin-left">
                          {date.slice(5)}
                        </span>
                      </div>
                    );
                  })}
              </div>
            </div>

            {/* Top Users */}
            <div className="bg-[#1A1A1A] rounded-2xl p-4">
              <h2 className="text-white font-semibold mb-3">Top Users by Games</h2>
              <div className="space-y-2">
                {(activity.top_users || []).map((user, index) => (
                  <div key={user._id} className="flex items-center gap-3">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                      index === 0 ? 'bg-[#FFD700] text-black' :
                      index === 1 ? 'bg-[#C0C0C0] text-black' :
                      index === 2 ? 'bg-[#CD7F32] text-black' : 'bg-[#333] text-white'
                    }`}>
                      {index + 1}
                    </span>
                    <span className="text-gray-300 flex-1 truncate">{user.email}</span>
                    <span className="text-[#00E5E5] font-semibold">{user.game_count} games</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Discounts Tab */}
        {activeTab === 'discounts' && (
          <div className="space-y-4">
            {/* Create Button */}
            <button
              onClick={() => setShowDiscountModal(true)}
              className="w-full bg-[#00E5E5] text-black font-bold py-3 rounded-xl flex items-center justify-center gap-2"
            >
              <Plus className="w-5 h-5" /> Create Discount Code
            </button>

            {/* Discount Codes List */}
            {discountCodes.length === 0 ? (
              <div className="text-center py-10">
                <Tag className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400">No discount codes yet</p>
              </div>
            ) : (
              <div className="space-y-2">
                {discountCodes.map(code => (
                  <div key={code.id} className={`rounded-xl p-4 ${!code.is_active ? 'opacity-50' : ''} ${code.is_free_account ? 'bg-gradient-to-r from-[#4ADE80]/20 to-[#1A1A1A] border border-[#4ADE80]/30' : 'bg-[#1A1A1A]'}`}>
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-xl font-bold text-[#00E5E5]">{code.code}</span>
                          {code.is_free_account && (
                            <span className="text-xs bg-[#4ADE80] text-black px-2 py-0.5 rounded font-bold">FREE ACCOUNT</span>
                          )}
                          {!code.is_active && (
                            <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded">INACTIVE</span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-3 mt-2 text-sm">
                          {code.is_free_account ? (
                            <span className="flex items-center gap-1 text-[#4ADE80]">
                              <Gift className="w-4 h-4" /> {code.free_account_days} days free
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-gray-400">
                              <Percent className="w-4 h-4" /> {code.discount_percent}% off
                            </span>
                          )}
                          {code.max_uses && (
                            <span className="flex items-center gap-1 text-gray-400">
                              <Users className="w-4 h-4" /> {code.times_used}/{code.max_uses} used
                            </span>
                          )}
                          {code.expires_at && (
                            <span className="flex items-center gap-1 text-gray-400">
                              <Calendar className="w-4 h-4" /> Expires {format(new Date(code.expires_at), 'MMM d')}
                            </span>
                          )}
                          {code.free_trial_games && !code.is_free_account && (
                            <span className="flex items-center gap-1 text-[#4ADE80]">
                              <Gift className="w-4 h-4" /> +{code.free_trial_games} free games
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleToggleDiscountCode(code.id, code.is_active)}
                          className={`p-2 rounded ${code.is_active ? 'text-yellow-400 hover:bg-yellow-400/10' : 'text-green-400 hover:bg-green-400/10'}`}
                          title={code.is_active ? 'Deactivate' : 'Activate'}
                        >
                          {code.is_active ? <Ban className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => handleDeleteDiscountCode(code.id)}
                          className="p-2 text-red-400 hover:bg-red-400/10 rounded"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create Discount Modal */}
      {showDiscountModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50">
          <div className="bg-[#1A1A1A] rounded-2xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-white">
                {newCode.is_free_account ? 'Create Free Account Code' : 'Create Discount Code'}
              </h2>
              <button onClick={() => setShowDiscountModal(false)} className="text-gray-400">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm text-gray-400 block mb-1">Code</label>
                <input
                  type="text"
                  placeholder="e.g., SUMMER25"
                  value={newCode.code}
                  onChange={(e) => setNewCode({ ...newCode, code: e.target.value.toUpperCase() })}
                  className="w-full bg-[#000] text-white rounded-xl py-3 px-4 focus:outline-none focus:ring-2 focus:ring-[#00E5E5]"
                  data-testid="discount-code-input"
                />
              </div>

              {!newCode.is_free_account && (
                <div>
                  <label className="text-sm text-gray-400 block mb-1">Discount Percent</label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={newCode.discount_percent}
                    onChange={(e) => setNewCode({ ...newCode, discount_percent: e.target.value })}
                    className="w-full bg-[#000] text-white rounded-xl py-3 px-4 focus:outline-none focus:ring-2 focus:ring-[#00E5E5]"
                    data-testid="discount-percent-input"
                  />
                </div>
              )}

              <div>
                <label className="text-sm text-gray-400 block mb-1">Max Uses (optional)</label>
                <input
                  type="number"
                  placeholder="Unlimited"
                  value={newCode.max_uses}
                  onChange={(e) => setNewCode({ ...newCode, max_uses: e.target.value })}
                  className="w-full bg-[#000] text-white rounded-xl py-3 px-4 focus:outline-none focus:ring-2 focus:ring-[#00E5E5]"
                />
              </div>

              <div>
                <label className="text-sm text-gray-400 block mb-1">Expires At (optional)</label>
                <input
                  type="date"
                  value={newCode.expires_at}
                  onChange={(e) => setNewCode({ ...newCode, expires_at: e.target.value })}
                  className="w-full bg-[#000] text-white rounded-xl py-3 px-4 focus:outline-none focus:ring-2 focus:ring-[#00E5E5]"
                />
              </div>

              <div>
                <label className="text-sm text-gray-400 block mb-1">Bonus Free Games (optional)</label>
                <input
                  type="number"
                  placeholder="0"
                  value={newCode.free_trial_games}
                  onChange={(e) => setNewCode({ ...newCode, free_trial_games: e.target.value })}
                  className="w-full bg-[#000] text-white rounded-xl py-3 px-4 focus:outline-none focus:ring-2 focus:ring-[#00E5E5]"
                />
              </div>

              {/* Free Account Toggle */}
              <div className="bg-gradient-to-r from-[#4ADE80]/20 to-[#000] rounded-xl p-4 border border-[#4ADE80]/30">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Gift className="w-5 h-5 text-[#4ADE80]" />
                    <span className="text-white font-semibold">Free Account Code</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setNewCode({ ...newCode, is_free_account: !newCode.is_free_account })}
                    className={`w-12 h-6 rounded-full transition-colors ${
                      newCode.is_free_account ? 'bg-[#4ADE80]' : 'bg-[#333]'
                    }`}
                    data-testid="free-account-toggle"
                  >
                    <div className={`w-5 h-5 rounded-full bg-white shadow transition-transform ${
                      newCode.is_free_account ? 'translate-x-6' : 'translate-x-0.5'
                    }`} />
                  </button>
                </div>
                <p className="text-xs text-gray-400 mb-3">
                  Grants full subscription access without payment. Perfect for promotions or beta testers.
                </p>
                
                {newCode.is_free_account && (
                  <div className="space-y-3 pt-3 border-t border-[#4ADE80]/20">
                    <div>
                      <label className="text-sm text-gray-400 block mb-1">Subscription Duration (days)</label>
                      <input
                        type="number"
                        min="1"
                        value={newCode.free_account_days}
                        onChange={(e) => setNewCode({ ...newCode, free_account_days: e.target.value })}
                        className="w-full bg-[#000] text-white rounded-xl py-3 px-4 focus:outline-none focus:ring-2 focus:ring-[#4ADE80]"
                        data-testid="free-account-days-input"
                      />
                    </div>
                    <div className="flex gap-2">
                      {[30, 90, 180, 365].map(days => (
                        <button
                          key={days}
                          type="button"
                          onClick={() => setNewCode({ ...newCode, free_account_days: days })}
                          className={`flex-1 py-2 rounded-lg text-sm font-semibold ${
                            parseInt(newCode.free_account_days) === days 
                              ? 'bg-[#4ADE80] text-black' 
                              : 'bg-[#333] text-white hover:bg-[#444]'
                          }`}
                        >
                          {days}d
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowDiscountModal(false)}
                className="flex-1 bg-[#333] text-white font-semibold py-3 rounded-xl"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateDiscountCode}
                className="flex-1 bg-[#00E5E5] text-black font-bold py-3 rounded-xl"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Management Modal */}
      {showUserModal && selectedUser && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-[#1A1A1A] rounded-2xl p-6 w-full max-w-lg my-8 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-white">Manage User</h2>
              <button onClick={() => setShowUserModal(false)} className="text-gray-400">
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* User Info */}
            <div className="bg-[#000] rounded-xl p-4 mb-4">
              <p className="text-white font-semibold text-lg">{selectedUser.email}</p>
              <div className="flex items-center gap-2 mt-1">
                {selectedUser.is_subscribed ? (
                  <span className="bg-[#4ADE80] text-black text-xs font-bold px-2 py-0.5 rounded">SUBSCRIBED</span>
                ) : (
                  <span className="bg-gray-600 text-white text-xs font-bold px-2 py-0.5 rounded">FREE</span>
                )}
                {selectedUser.subscription_type && (
                  <span className="text-gray-400 text-sm">{selectedUser.subscription_type.replace(/_/g, ' ')}</span>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {selectedUser.athlete_count || 0} athletes • {selectedUser.game_count || 0} games
              </p>
            </div>

            {/* Sports Selection */}
            <div className="bg-[#000] rounded-xl p-4 mb-4">
              <p className="text-sm text-white font-semibold mb-2">
                Allowed Sports 
                <span className="text-gray-400 font-normal ml-2">
                  ({editingSports.length}/{PLAN_SPORTS_LIMITS[selectedUser.subscription_type] || 6})
                </span>
              </p>
              <div className="grid grid-cols-6 gap-2 mb-3">
                {ENABLED_SPORTS.map(sport => {
                  const config = SPORTS_CONFIG[sport];
                  const isSelected = editingSports.includes(sport);
                  return (
                    <button
                      key={sport}
                      onClick={() => handleToggleUserSport(sport)}
                      className={`p-2 rounded-lg transition-all ${
                        isSelected 
                          ? 'bg-[#00E5E5]/20 border-2 border-[#00E5E5]' 
                          : 'bg-[#333] border-2 border-transparent'
                      }`}
                      title={config?.name}
                    >
                      <span className="text-xl">{config?.emoji}</span>
                    </button>
                  );
                })}
              </div>
              <button
                onClick={handleUpdateUserSports}
                className="w-full bg-[#00E5E5] text-black font-semibold py-2 rounded-lg text-sm"
              >
                Save Sports
              </button>
            </div>

            {/* Free Upgrade - Reward Account */}
            <div className="bg-gradient-to-r from-[#4ADE80]/20 to-[#000] rounded-xl p-4 mb-4 border border-[#4ADE80]/30">
              <div className="flex items-center gap-2 mb-2">
                <Gift className="w-4 h-4 text-[#4ADE80]" />
                <p className="text-sm text-white font-semibold">Free Upgrade (Reward)</p>
              </div>
              <p className="text-xs text-gray-400 mb-3">Grant premium access without payment</p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { plan: '2_sports_monthly', label: '2 Sports', days: 30 },
                  { plan: '4_sports_monthly', label: '4 Sports', days: 30 },
                  { plan: 'all_sports_monthly', label: 'All Sports (1mo)', days: 30 },
                  { plan: 'all_sports_yearly', label: 'All Sports (1yr)', days: 365 },
                ].map(({ plan, label, days }) => (
                  <button
                    key={plan}
                    onClick={() => handleManageSubscription('free_upgrade', days, plan)}
                    className="bg-[#4ADE80]/20 text-[#4ADE80] py-2 px-3 rounded-lg text-xs hover:bg-[#4ADE80] hover:text-black font-semibold"
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Extend Subscription */}
            {selectedUser.is_subscribed && (
              <div className="bg-[#000] rounded-xl p-4 mb-4">
                <p className="text-sm text-white font-semibold mb-2">Extend Subscription</p>
                <div className="flex gap-2">
                  {[7, 30, 90, 365].map(days => (
                    <button
                      key={days}
                      onClick={() => handleManageSubscription('extend', days)}
                      className="flex-1 bg-[#333] text-white py-2 rounded-lg text-sm hover:bg-[#00E5E5] hover:text-black"
                    >
                      +{days}d
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Change Plan */}
            {selectedUser.is_subscribed && (
              <div className="bg-[#000] rounded-xl p-4 mb-4">
                <p className="text-sm text-white font-semibold mb-2">Change Plan</p>
                <div className="grid grid-cols-2 gap-2">
                  {['2_sports_monthly', '4_sports_monthly', 'all_sports_monthly', 'all_sports_yearly'].map(plan => (
                    <button
                      key={plan}
                      onClick={() => handleManageSubscription('change_plan', null, plan)}
                      className="bg-[#333] text-white py-2 px-3 rounded-lg text-xs hover:bg-[#00E5E5] hover:text-black capitalize"
                    >
                      {plan.replace(/_/g, ' ')}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Danger Zone */}
            <div className="bg-red-500/10 rounded-xl p-4 border border-red-500/30">
              <p className="text-sm text-red-400 font-semibold mb-3">Danger Zone</p>
              <div className="space-y-2">
                {selectedUser.is_subscribed && (
                  <button
                    onClick={() => handleManageSubscription('cancel')}
                    className="w-full bg-[#333] text-red-400 py-2 rounded-lg font-semibold hover:bg-red-500/20 text-sm"
                  >
                    Cancel Subscription
                  </button>
                )}
                <button
                  onClick={() => {
                    if (window.confirm(`Are you sure you want to archive ${selectedUser.email}? This will disable their account.`)) {
                      handleArchiveUser();
                    }
                  }}
                  className="w-full bg-[#333] text-orange-400 py-2 rounded-lg font-semibold hover:bg-orange-500/20 text-sm"
                >
                  Archive Account
                </button>
                <button
                  onClick={() => {
                    if (window.confirm(`PERMANENTLY DELETE ${selectedUser.email} and all their data? This cannot be undone!`)) {
                      handleDeleteUser(selectedUser.id);
                      setShowUserModal(false);
                    }
                  }}
                  className="w-full bg-red-500/20 text-red-400 py-2 rounded-lg font-semibold hover:bg-red-500/30 text-sm"
                >
                  Delete Account Permanently
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
